import type {
  CreateDisputedFindingsInput,
  CreateUbClaimFindingsInput,
  DeleteStageTrackerBlockerInput,
  OpenStageTrackerBlockerInput,
  SetAssigneesInput,
  SetClaimAuditTypesInput,
  SetClaimAppealedInput,
  SetClaimDisputedInput,
  SetClaimStageInput,
  SetClaimTechDenialsInput,
  SetClientSignOffClaimStatesInput,
  SetDocumentationClaimStatesInput,
  SetDueDatesInput,
  SetFindingStatusInput,
  SetInvoiceClaimStatesInput,
  SetNegotiationClaimStatesInput,
  SetOnHoldClaimStatesInput,
  SetProviderSignOffClaimStatesInput,
  SetStageTrackerBlockerSegmentInput,
  SetWorkflowClaimStatesInput,
  SaveClaimRoleAssignmentsInput,
  GetClaimRoleAssignmentsQuery,
  SaveClaimRoleAssignmentsMutation,
  createBatchClaimCommentMutation,
  createDisputedFindingsMutation,
  createUbClaimFindingsMutation,
  deleteStageTrackerBlockerMutation,
  getBatchClaimAssigneeAuthorAndProviderInfoQuery,
  getBatchClaimAuditDocumentationQuery,
  getBatchClaimCommentsQuery,
  getBatchClaimEventHistoryTimelineQuery,
  getBatchClaimEventHistoryTimelineQueryVariables,
  getBatchClaimHeaderInfoQuery,
  getClaimAuditTypesQuery,
  getClaimPhasesQuery,
  openStageTrackerBlockerMutation,
  batchClaimDocumentationStateQueryQuery,
  setAssigneesMutation,
  setClaimAuditTypesMutation,
  setClaimAppealedMutation,
  setClaimDisputedMutation,
  setClaimStageMutation,
  setClaimTechDenialsMutation,
  setClientSignOffClaimStatesMutation,
  setDocumentationClaimStatesMutation,
  setDueDatesMutation,
  setFindingStatusMutation,
  setInvoiceClaimStatesMutation,
  setNegotiationClaimStatesMutation,
  setOnHoldClaimStatesMutation,
  setProviderSignOffClaimStatesMutation,
  setStageTrackerBlockerSegmentMutation,
  setWorkflowClaimStatesMutation,
} from "../../remote/gql/graphql";
import type {
  ClaimLifecycleAggregatesQuery,
  ClaimLifecycleAggregatesQueryVariables,
} from "../../remote/queries/getClaimLifecycleAggregates.gql";
import type { ClientReturnType } from "../types";
import type { AlaffiaClientModuleArgs } from "./modules";

import { createBatchClaimCommentMutationDoc } from "../../remote/mutations/createBatchClaimComment.gql";
import { createDisputedFindingsMutationDoc } from "../../remote/mutations/createDisputedFindings.gql";
import { createUbClaimFindingsMutationDoc } from "../../remote/mutations/createUbClaimFindings.gql";
import { deleteStageTrackerBlockerMutationDoc } from "../../remote/mutations/deleteStageTrackerBlocker.gql";
import { openStageTrackerBlockerMutationDoc } from "../../remote/mutations/openStageTrackerBlocker.gql";
import { setAssigneesMutationDoc } from "../../remote/mutations/setAssignees.gql";
import { setClaimAppealedMutationDoc } from "../../remote/mutations/setClaimAppealed.gql";
import { setClaimAuditTypesMutationDoc } from "../../remote/mutations/setClaimAuditTypes.gql";
import { setClaimDisputedMutationDoc } from "../../remote/mutations/setClaimDisputed.gql";
import { setClaimStageMutationDoc } from "../../remote/mutations/setClaimStage.gql";
import { setClaimTechDenialsMutationDoc } from "../../remote/mutations/setClaimTechDenials.gql";
import { setClientSignOffClaimStatesMutationDoc } from "../../remote/mutations/setClientSignOffClaimStates.gql";
import { setDocumentationClaimStatesMutationDoc } from "../../remote/mutations/setDocumentationClaimStates.gql";
import { setDueDatesMutationDoc } from "../../remote/mutations/setDueDates.gql";
import { setFindingStatusMutationDoc } from "../../remote/mutations/setFindingStatus.gql";
import { setInvoiceClaimStatesMutationDoc } from "../../remote/mutations/setInvoiceClaimStates.gql";
import { setNegotiationClaimStatesMutationDoc } from "../../remote/mutations/setNegotiationClaimStates.gql";
import { setOnHoldClaimStatesMutationDoc } from "../../remote/mutations/setOnHoldClaimStates.gql";
import { setProviderSignOffClaimStatesMutationDoc } from "../../remote/mutations/setProviderSignOffClaimStates.gql";
import { setStageTrackerBlockerSegmentMutationDoc } from "../../remote/mutations/setStageTrackerBlockerSegment.gql";
import { setWorkflowClaimStatesMutationDoc } from "../../remote/mutations/setWorkflowClaimStates.gql";
import { batchClaimDocumentationStateQueryDoc } from "../../remote/queries/batchClaimDocumentationState.gql";
import {
  getClaimRoleAssignmentsDoc,
  saveClaimRoleAssignmentsDoc,
} from "../../remote/queries/claimRoleAssignments.gql";
import { getBatchClaimAssigneeAuthorAndProviderInfoQueryDoc } from "../../remote/queries/getBatchClaimAssigneeAuthorAndProviderInfo.gql";
import { getBatchClaimAuditDocumentationQueryDoc } from "../../remote/queries/getBatchClaimAuditDocumentation.gql";
import { getBatchClaimCommentsQueryDoc } from "../../remote/queries/getBatchClaimComments.gql";
import { getBatchClaimEventHistoryTimelineQueryDoc } from "../../remote/queries/getBatchClaimEventHistoryTimeline.gql";
import { getBatchClaimHeaderInfoQueryDoc } from "../../remote/queries/getBatchClaimHeaderInfo.gql";
import { getClaimAuditTypesQueryDoc } from "../../remote/queries/getClaimAuditTypes.gql";
import {
  getClaimLifecycleAggregatesForProvidersQueryDoc,
  getClaimLifecycleAggregatesQueryDoc,
} from "../../remote/queries/getClaimLifecycleAggregates.gql";
import { getClaimPhasesQueryDoc } from "../../remote/queries/getClaimPhases.gql";
import { AlaffiaClientModule } from "./modules";

type BatchClaimsModuleArgs = AlaffiaClientModuleArgs;

export class BatchClaimsModule extends AlaffiaClientModule {
  constructor(args: BatchClaimsModuleArgs) {
    super(args);
  }

  createBatchClaimComment = async (input: {
    claimId: string;
    body: string;
  }): ClientReturnType<
    createBatchClaimCommentMutation["createBatchClaimComment"]
  > => {
    const result = await this.runGqlMutation(
      createBatchClaimCommentMutationDoc,
      input,
      "createBatchClaimComment",
    );

    return {
      data: result.data?.createBatchClaimComment ?? null,
      error: result.error ?? null,
    };
  };

  createDisputedFindings = async ({
    input,
  }: {
    input: CreateDisputedFindingsInput;
  }): ClientReturnType<
    createDisputedFindingsMutation["createDisputedFindings"]
  > => {
    const result = await this.runGqlMutation(
      createDisputedFindingsMutationDoc,
      { input },
      "createDisputedFindings",
    );

    return {
      data: result.data?.createDisputedFindings ?? null,
      error: result.error ?? null,
    };
  };

  createUbClaimFindings = async ({
    input,
  }: {
    input: CreateUbClaimFindingsInput;
  }): ClientReturnType<
    createUbClaimFindingsMutation["createUbClaimFindings"]
  > => {
    const result = await this.runGqlMutation(
      createUbClaimFindingsMutationDoc,
      { input },
      "createUbClaimFindings",
    );

    return {
      data: result.data?.createUbClaimFindings ?? null,
      error: result.error ?? null,
    };
  };

  getClaimLifecycleAggregates = async (input: {
    batchClaimFilter: ClaimLifecycleAggregatesQueryVariables["batchClaimFilter"];
    providerOnly?: boolean;
  }): ClientReturnType<ClaimLifecycleAggregatesQuery> => {
    const queryDoc = input.providerOnly
      ? getClaimLifecycleAggregatesForProvidersQueryDoc
      : getClaimLifecycleAggregatesQueryDoc;

    const result = await this.runGqlQuery(
      queryDoc,
      { batchClaimFilter: input.batchClaimFilter },
      "getClaimLifecycleAggregates",
    );

    return {
      data: result.data ?? null,
      error: result.error ?? null,
    };
  };

  getBatchClaimAssigneeAuthorAndProviderInfo = async (input: {
    batchClaimId: string;
    includeAdminFields: boolean;
  }): ClientReturnType<
    getBatchClaimAssigneeAuthorAndProviderInfoQuery["batchClaim"]
  > => {
    const result = await this.runGqlQuery(
      getBatchClaimAssigneeAuthorAndProviderInfoQueryDoc,
      {
        batchClaimId: input.batchClaimId,
        includeAdminFields: input.includeAdminFields,
      },
      "getBatchClaimAssigneeAuthorAndProviderInfo",
    );

    return {
      data: result.data?.batchClaim ?? null,
      error: result.error ?? null,
    };
  };

  getBatchClaimAuditDocumentation = async (input: {
    batchClaimId: string;
  }): ClientReturnType<getBatchClaimAuditDocumentationQuery["batchClaim"]> => {
    const result = await this.runGqlQuery(
      getBatchClaimAuditDocumentationQueryDoc,
      { batchClaimId: input.batchClaimId },
      "getBatchClaimAuditDocumentation",
    );

    return {
      data: result.data?.batchClaim ?? null,
      error: result.error ?? null,
    };
  };

  batchClaimDocumentationState = async (input: {
    batchClaimIds: string[];
  }): ClientReturnType<
    NonNullable<batchClaimDocumentationStateQueryQuery["batchClaims"]>["nodes"]
  > => {
    const result = await this.runGqlQuery(
      batchClaimDocumentationStateQueryDoc,
      { batchClaimIds: input.batchClaimIds },
      "batchClaimDocumentationState",
    );

    return {
      data: result.data?.batchClaims?.nodes ?? null,
      error: result.error ?? null,
    };
  };

  getBatchClaimComments = async (input: {
    batchClaimId: string;
  }): ClientReturnType<
    NonNullable<getBatchClaimCommentsQuery["batchClaim"]>["comments"]
  > => {
    const result = await this.runGqlQuery(
      getBatchClaimCommentsQueryDoc,
      { claimId: input.batchClaimId },
      "getBatchClaimComments",
    );

    const comments = result.data?.batchClaim?.comments ?? null;

    return {
      data: comments,
      error: result.error ?? null,
    };
  };

  getBatchClaimEventHistoryTimeline = async (
    {
      batchClaimId,
    }: Pick<getBatchClaimEventHistoryTimelineQueryVariables, "batchClaimId">,
    pagination: Pick<
      getBatchClaimEventHistoryTimelineQueryVariables,
      "limit" | "offset"
    >,
  ): ClientReturnType<
    NonNullable<
      getBatchClaimEventHistoryTimelineQuery["batchClaim"]
    >["eventHistoryTimeline"]
  > => {
    const result = await this.runGqlQuery(
      getBatchClaimEventHistoryTimelineQueryDoc,
      { batchClaimId, ...pagination },
      "getBatchClaimEventHistoryTimeline",
    );

    return {
      data: result.data?.batchClaim?.eventHistoryTimeline ?? null,
      error: result.error ?? null,
    };
  };

  getBatchClaimHeaderInfo = async (input: {
    batchClaimId: string;
    shouldShowExtendedFields: boolean;
  }): ClientReturnType<getBatchClaimHeaderInfoQuery["batchClaim"]> => {
    const result = await this.runGqlQuery(
      getBatchClaimHeaderInfoQueryDoc,
      {
        batchClaimId: input.batchClaimId,
        shouldShowExtendedFields: input.shouldShowExtendedFields,
      },
      "getBatchClaimHeaderInfo",
    );

    return {
      data: result.data?.batchClaim ?? null,
      error: result.error ?? null,
    };
  };

  getClaimAuditTypes = async (): ClientReturnType<
    getClaimAuditTypesQuery["claimAuditTypes"]
  > => {
    const result = await this.runGqlQuery(
      getClaimAuditTypesQueryDoc,
      {},
      "getClaimAuditTypes",
    );

    return {
      data: result.data?.claimAuditTypes ?? null,
      error: result.error ?? null,
    };
  };

  getClaimRoleAssignments = async (input: {
    claimId: string;
  }): ClientReturnType<
    GetClaimRoleAssignmentsQuery["claimRoleAssignments"]
  > => {
    const result = await this.runGqlQuery(
      getClaimRoleAssignmentsDoc,
      input,
      "getClaimRoleAssignments",
    );
    return {
      data: result.data?.claimRoleAssignments ?? null,
      error: result.error ?? null,
    };
  };

  saveClaimRoleAssignments = async (input: {
    input: SaveClaimRoleAssignmentsInput;
  }): ClientReturnType<
    SaveClaimRoleAssignmentsMutation["saveClaimRoleAssignments"]
  > => {
    const result = await this.runGqlMutation(
      saveClaimRoleAssignmentsDoc,
      input,
      "saveClaimRoleAssignments",
    );
    return {
      data: result.data?.saveClaimRoleAssignments ?? null,
      error: result.error ?? null,
    };
  };

  getClaimPhases = async (): ClientReturnType<
    getClaimPhasesQuery["claimPhases"]
  > => {
    const result = await this.runGqlQuery(
      getClaimPhasesQueryDoc,
      {},
      "getClaimPhases",
    );
    return {
      data: result.data?.claimPhases ?? null,
      error: result.error ?? null,
    };
  };

  openStageTrackerBlocker = async ({
    input,
  }: {
    input: OpenStageTrackerBlockerInput;
  }): ClientReturnType<
    openStageTrackerBlockerMutation["openStageTrackerBlocker"]
  > => {
    const result = await this.runGqlMutation(
      openStageTrackerBlockerMutationDoc,
      { input },
      "openStageTrackerBlocker",
    );

    return {
      data: result.data?.openStageTrackerBlocker ?? null,
      error: result.error ?? null,
    };
  };

  deleteStageTrackerBlocker = async ({
    input,
  }: {
    input: DeleteStageTrackerBlockerInput;
  }): ClientReturnType<
    deleteStageTrackerBlockerMutation["deleteStageTrackerBlocker"]
  > => {
    const result = await this.runGqlMutation(
      deleteStageTrackerBlockerMutationDoc,
      { input },
      "deleteStageTrackerBlocker",
    );

    return {
      data: result.data?.deleteStageTrackerBlocker ?? null,
      error: result.error ?? null,
    };
  };

  setStageTrackerBlockerSegment = async ({
    input,
  }: {
    input: SetStageTrackerBlockerSegmentInput;
  }): ClientReturnType<
    setStageTrackerBlockerSegmentMutation["setStageTrackerBlockerSegment"]
  > => {
    const result = await this.runGqlMutation(
      setStageTrackerBlockerSegmentMutationDoc,
      { input },
      "setStageTrackerBlockerSegment",
    );

    return {
      data: result.data?.setStageTrackerBlockerSegment ?? null,
      error: result.error ?? null,
    };
  };

  setAssignees = async ({
    input,
  }: {
    input: SetAssigneesInput;
  }): ClientReturnType<setAssigneesMutation["setAssignees"]> => {
    const result = await this.runGqlMutation(
      setAssigneesMutationDoc,
      { input },
      "setAssignees",
    );

    return {
      data: result.data?.setAssignees ?? null,
      error: result.error ?? null,
    };
  };

  setClaimAuditTypes = async ({
    input,
  }: {
    input: SetClaimAuditTypesInput;
  }): ClientReturnType<setClaimAuditTypesMutation["setClaimAuditTypes"]> => {
    const result = await this.runGqlMutation(
      setClaimAuditTypesMutationDoc,
      { input },
      "setClaimAuditTypes",
    );

    return {
      data: result.data?.setClaimAuditTypes ?? null,
      error: result.error ?? null,
    };
  };

  setClaimAppealed = async ({
    input,
  }: {
    input: SetClaimAppealedInput;
  }): ClientReturnType<setClaimAppealedMutation["setClaimAppealed"]> => {
    const result = await this.runGqlMutation(
      setClaimAppealedMutationDoc,
      { input },
      "setClaimAppealed",
    );

    return {
      data: result.data?.setClaimAppealed ?? null,
      error: result.error ?? null,
    };
  };

  setClaimDisputed = async ({
    input,
  }: {
    input: SetClaimDisputedInput;
  }): ClientReturnType<setClaimDisputedMutation["setClaimDisputed"]> => {
    const result = await this.runGqlMutation(
      setClaimDisputedMutationDoc,
      { input },
      "setClaimDisputed",
    );

    return {
      data: result.data?.setClaimDisputed ?? null,
      error: result.error ?? null,
    };
  };

  setClaimStage = async ({
    input,
  }: {
    input: SetClaimStageInput;
  }): ClientReturnType<setClaimStageMutation["setClaimStage"]> => {
    const result = await this.runGqlMutation(
      setClaimStageMutationDoc,
      { input },
      "setClaimStage",
    );

    return {
      data: result.data?.setClaimStage ?? null,
      error: result.error ?? null,
    };
  };

  setClaimTechDenials = async ({
    input,
  }: {
    input: SetClaimTechDenialsInput;
  }): ClientReturnType<setClaimTechDenialsMutation["setClaimTechDenials"]> => {
    const result = await this.runGqlMutation(
      setClaimTechDenialsMutationDoc,
      { input },
      "setClaimTechDenials",
    );

    return {
      data: result.data?.setClaimTechDenials ?? null,
      error: result.error ?? null,
    };
  };

  setClientSignOffClaimStates = async ({
    input,
  }: {
    input: SetClientSignOffClaimStatesInput;
  }): ClientReturnType<
    setClientSignOffClaimStatesMutation["setClientSignOffClaimStates"]
  > => {
    const result = await this.runGqlMutation(
      setClientSignOffClaimStatesMutationDoc,
      { input },
      "setClientSignOffClaimStates",
    );

    return {
      data: result.data?.setClientSignOffClaimStates ?? null,
      error: result.error ?? null,
    };
  };

  setDocumentationClaimStates = async ({
    input,
  }: {
    input: SetDocumentationClaimStatesInput;
  }): ClientReturnType<
    setDocumentationClaimStatesMutation["setDocumentationClaimStates"]
  > => {
    const result = await this.runGqlMutation(
      setDocumentationClaimStatesMutationDoc,
      { input },
      "setDocumentationClaimStates",
    );

    return {
      data: result.data?.setDocumentationClaimStates ?? null,
      error: result.error ?? null,
    };
  };

  setDueDates = async ({
    input,
  }: {
    input: SetDueDatesInput;
  }): ClientReturnType<setDueDatesMutation["setDueDates"]> => {
    const result = await this.runGqlMutation(
      setDueDatesMutationDoc,
      { input },
      "setDueDates",
    );

    return {
      data: result.data?.setDueDates ?? null,
      error: result.error ?? null,
    };
  };

  setFindingStatus = async ({
    input,
  }: {
    input: SetFindingStatusInput;
  }): ClientReturnType<setFindingStatusMutation["setFindingStatus"]> => {
    const result = await this.runGqlMutation(
      setFindingStatusMutationDoc,
      { input },
      "setFindingStatus",
    );

    return {
      data: result.data?.setFindingStatus ?? null,
      error: result.error ?? null,
    };
  };

  setInvoiceClaimStates = async ({
    input,
  }: {
    input: SetInvoiceClaimStatesInput;
  }): ClientReturnType<
    setInvoiceClaimStatesMutation["setInvoiceClaimStates"]
  > => {
    const result = await this.runGqlMutation(
      setInvoiceClaimStatesMutationDoc,
      { input },
      "setInvoiceClaimStates ",
    );

    return {
      data: result.data?.setInvoiceClaimStates ?? null,
      error: result.error ?? null,
    };
  };

  setNegotiationClaimStates = async ({
    input,
  }: {
    input: SetNegotiationClaimStatesInput;
  }): ClientReturnType<
    setNegotiationClaimStatesMutation["setNegotiationClaimStates"]
  > => {
    const result = await this.runGqlMutation(
      setNegotiationClaimStatesMutationDoc,
      { input },
      "setNegotiationClaimStates",
    );

    return {
      data: result.data?.setNegotiationClaimStates ?? null,
      error: result.error ?? null,
    };
  };

  setOnHoldClaimStates = async ({
    input,
  }: {
    input: SetOnHoldClaimStatesInput;
  }): ClientReturnType<
    setOnHoldClaimStatesMutation["setOnHoldClaimStates"]
  > => {
    const result = await this.runGqlMutation(
      setOnHoldClaimStatesMutationDoc,
      { input },
      "setOnHoldClaimStates ",
    );

    return {
      data: result.data?.setOnHoldClaimStates ?? null,
      error: result.error ?? null,
    };
  };

  setProviderSignOffClaimStates = async ({
    input,
  }: {
    input: SetProviderSignOffClaimStatesInput;
  }): ClientReturnType<
    setProviderSignOffClaimStatesMutation["setProviderSignOffClaimStates"]
  > => {
    const result = await this.runGqlMutation(
      setProviderSignOffClaimStatesMutationDoc,
      { input },
      "setProviderSignOffClaimStates",
    );

    return {
      data: result.data?.setProviderSignOffClaimStates ?? null,
      error: result.error ?? null,
    };
  };

  setWorkflowClaimStates = async ({
    input,
  }: {
    input: SetWorkflowClaimStatesInput;
  }): ClientReturnType<
    setWorkflowClaimStatesMutation["setWorkflowClaimStates"]
  > => {
    const result = await this.runGqlMutation(
      setWorkflowClaimStatesMutationDoc,
      { input },
      "setWorkflowClaimStates",
    );

    return {
      data: result.data?.setWorkflowClaimStates ?? null,
      error: result.error ?? null,
    };
  };
}
